import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, RefreshCw, AlertCircle, ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import { useApi } from '../hooks/useApi';

const DISPLAY_ORDER = [
  'Meteologica',
  'Website',
  'v3.1.0 model',
  'v2.0.0 model',
  'v1.0.0-fix2 model',
];

const COLLAPSED_LINES = 5;
const EXPANDED_LINES = 200;
const LINE_STEP = 20;

const LogPanel = ({ log, expanded, visibleLines, onToggle, onLinesChange }) => {
  const ref = useRef(null);
  const allLines = (log.content || '').split('\n');
  const shownLines = expanded ? visibleLines : COLLAPSED_LINES;
  const visibleText = allLines.slice(-shownLines).join('\n');

  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [visibleText]);

  return (
    <div className={`border border-gray-200 rounded-xl overflow-hidden flex flex-col ${expanded ? 'col-span-full' : ''}`}>
      <div
        onClick={onToggle}
        className="bg-gray-50 px-3 py-2 flex items-center justify-between border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors select-none"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronUp className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />}
          <span className="font-semibold text-gray-800 text-sm truncate">{log.file_name}</span>
        </div>
        <span className="text-xs text-blue-600 font-medium flex-shrink-0 ml-2">
          {expanded ? 'Collapse' : 'Show more'}
        </span>
      </div>
      {expanded && (
        <div className="bg-gray-50 px-3 py-1 flex items-center justify-between border-b border-gray-200">
          <span className="text-xs text-gray-400 font-mono truncate">{log.path}</span>
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
            <span className="text-xs text-gray-600 font-medium">Lines:</span>
            <button
              onClick={() => onLinesChange(Math.max(LINE_STEP, visibleLines - LINE_STEP))}
              className="p-0.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>
            <input
              type="number"
              min={LINE_STEP}
              value={visibleLines}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => onLinesChange(Math.max(LINE_STEP, Number(e.target.value) || LINE_STEP))}
              className="w-16 px-1.5 py-0.5 border border-gray-300 rounded text-xs text-center focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              onClick={() => onLinesChange(visibleLines + LINE_STEP)}
              className="p-0.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
      <pre
        ref={ref}
        className="px-3 py-2 text-[11px] leading-snug font-mono bg-white text-gray-800 overflow-auto whitespace-pre flex-1"
        style={{ maxHeight: expanded ? '500px' : '105px' }}
      >{visibleText || '[empty]'}</pre>
    </div>
  );
};

const Logs = () => {
  const { fetchGet } = useApi();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [linesCounts, setLinesCounts] = useState({});

  const orderLogs = (rawLogs) => {
    const ordered = [];
    const byName = {};
    rawLogs.forEach((l) => { byName[l.file_name] = l; });
    DISPLAY_ORDER.forEach((name) => {
      if (byName[name]) ordered.push(byName[name]);
    });
    rawLogs.forEach((l) => {
      if (!DISPLAY_ORDER.includes(l.file_name)) ordered.push(l);
    });
    return ordered;
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetchGet(`/logs?lines=${EXPANDED_LINES}`);
      const data = await resp.json();
      if (data.status === 'success') {
        setLogs(orderLogs(data.logs));
      } else {
        setError(data.message || 'Unknown error');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchGet]);

  const fetchSingleLog = useCallback(async (logId, n) => {
    try {
      const resp = await fetchGet(`/logs/${logId}?lines=${n}`);
      const data = await resp.json();
      if (data.status === 'success') {
        setLogs((prev) => prev.map((l) => l.id === logId ? data.log : l));
      }
    } catch (e) {
      // keep existing content on error
    }
  }, [fetchGet]);

  useEffect(() => {
    fetchLogs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleLinesChange = (id, n) => {
    setLinesCounts((prev) => ({ ...prev, [id]: n }));
    fetchSingleLog(id, n);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <FileText className="w-7 h-7" />
          Logs
        </h1>
        <button
          onClick={() => fetchLogs()}
          disabled={loading}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-900 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="mb-3 p-3 bg-red-100 text-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {logs.map((log) => (
          <LogPanel
            key={log.id}
            log={log}
            expanded={expandedIds.has(log.id)}
            visibleLines={linesCounts[log.id] || EXPANDED_LINES}
            onToggle={() => toggleExpanded(log.id)}
            onLinesChange={(n) => handleLinesChange(log.id, n)}
          />
        ))}
      </div>

      {!loading && logs.length === 0 && !error && (
        <p className="text-gray-500 text-center py-8">No log sources configured.</p>
      )}
    </div>
  );
};

export default Logs;
